import * as React from 'react'
import Image from 'next/image'
import { Plus, X } from 'lucide-react'

import { ChampionIcon } from './champion-icon'

import { cn } from '@/lib/utils'

import type { BracketTheme } from './types'

type TeamSlotProps = {
  name: string
  image?: string | null
  placeholder: string
  isWinner: boolean
  autoWin: boolean
  autoLabel: string
  onPick: () => void
  editable?: boolean
  onRename?: (value: string) => void
  onRequestImage?: () => void
  onClearImage?: () => void
  showName?: boolean
  showImage?: boolean
  theme: BracketTheme
}

export function TeamSlot({
  name,
  image,
  placeholder,
  isWinner,
  autoWin,
  autoLabel,
  onPick,
  editable = false,
  onRename,
  onRequestImage,
  onClearImage,
  showName = true,
  showImage = true,
  theme,
}: TeamSlotProps) {
  const empty = name.trim().length === 0
  const displayName = empty ? placeholder : name
  const showExternalName = showImage && showName
  const showTextInSlot = !showImage
  const [isEditing, setIsEditing] = React.useState(false)
  const [draft, setDraft] = React.useState(name)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!isEditing) {
      setDraft(name)
    }
  }, [isEditing, name])

  React.useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  React.useEffect(() => {
    if (!showExternalName && !showTextInSlot && isEditing) {
      setIsEditing(false)
    }
  }, [showExternalName, showTextInSlot, isEditing])

  const save = () => {
    if (!editable || !onRename) {
      setIsEditing(false)
      return
    }
    onRename(draft.trim())
    setIsEditing(false)
  }

  const cancel = () => {
    setDraft(name)
    setIsEditing(false)
  }

  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClearImage?.()
  }

  return (
    <div className={cn(theme.slotRoot, empty && 'text-muted-foreground/50')}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          if (isEditing) return
          onPick()
        }}
        onKeyDown={(event) => {
          if (isEditing) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onPick()
          }
        }}
        className={cn(
          theme.slotBase,
          showTextInSlot && theme.slotTextBase,
          isWinner ? theme.slotWinner : theme.slotDefault,
          empty && theme.slotEmpty,
        )}
      >
        <div className="relative flex h-full w-full items-center justify-center">
          {showImage ? (
            <>
              {image ? (
                <Image
                  unoptimized
                  src={image}
                  alt={displayName}
                  fill
                  sizes="(min-width: 640px) 112px, 96px"
                  className="object-cover"
                />
              ) : editable && onRequestImage ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    onRequestImage()
                  }}
                  className="flex h-full w-full items-center justify-center text-primary/60 transition-colors hover:bg-muted/30 hover:text-primary"
                >
                  <Plus className="h-8 w-8 stroke-[3] text-primary/60 transition-colors group-hover/slot:text-primary" />
                </button>
              ) : (
                <div className={theme.slotPlaceholder}>
                  {/* Minimal Placehoder */}
                </div>
              )}

              <div
                className={cn(
                  theme.slotImageOverlay,
                  !image && 'group-hover/slot:bg-transparent',
                  image && theme.slotImageOverlayVisible,
                )}
              />
            </>
          ) : editable ? (
            isEditing ? (
              <input
                ref={inputRef}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={save}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    save()
                  } else if (event.key === 'Escape') {
                    event.preventDefault()
                    cancel()
                  }
                }}
                className={theme.slotTextInput}
                placeholder={placeholder}
              />
            ) : (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  setIsEditing(true)
                }}
                className={theme.slotTextNameButton}
                title={displayName}
              >
                {displayName}
              </button>
            )
          ) : (
            <span className={theme.slotTextName} title={displayName}>
              {displayName}
            </span>
          )}

          {isWinner ? (
            <ChampionIcon
              size={16}
              className="absolute left-1.5 top-1.5 drop-shadow-sm"
            />
          ) : null}

          {autoWin && isWinner ? (
            <span className={theme.slotAutoWinBadge}>{autoLabel}</span>
          ) : null}

          {/* {editable && onRequestImage ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                onRequestImage()
              }}
              className="absolute right-2 bottom-2 text-[10px] font-semibold text-foreground/80 hover:text-primary"
              title="更换图片"
            >
              替换
            </button>
          ) : null} */}

          {showImage && image && onClearImage && (
            <button
              type="button"
              onClick={handleRemoveImage}
              className={theme.slotImageRemoveButton}
              title="Remove image"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {showImage ? (
        <div className="flex min-h-[24px] max-w-[120px] items-center justify-center px-1 text-center">
          {showExternalName ? (
            editable ? (
              isEditing ? (
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onBlur={save}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      save()
                    } else if (event.key === 'Escape') {
                      event.preventDefault()
                      cancel()
                    }
                  }}
                  className="w-full rounded-sm border-b border-primary bg-transparent py-0.5 text-center text-sm font-medium outline-none focus:border-b-2"
                  placeholder={placeholder}
                />
              ) : (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    setIsEditing(true)
                  }}
                  className={theme.slotNameButton}
                  title={displayName}
                >
                  {displayName}
                </button>
              )
            ) : (
              <span className={theme.slotName} title={displayName}>
                {displayName}
              </span>
            )
          ) : (
            <span className="w-full select-none text-xs font-semibold uppercase tracking-wide text-muted-foreground/50"></span>
          )}
        </div>
      ) : null}
    </div>
  )
}
